import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../api/client";

// ── Tipos compartidos ─────────────────────────────────────

export interface RestaurantInfo {
  id: string;
  slug: string;
  name: string;
  razon_social: string | null;
  logo_url: string | null;
  commercial_rows: number;
  last_data_date: string | null;
}

export interface MesDisponible {
  anio: number;
  mm: number;
  label: string;
  n_dias: number;
}

// ── Contexto ──────────────────────────────────────────────

interface FilterCtx {
  // Datos
  restaurants: RestaurantInfo[];
  meses: MesDisponible[];

  // Selección actual (compartida entre los 3 tableros)
  selectedRestaurantId: string;
  selectedRestaurantSlug: string;
  mesSel: { anio: number; mm: number } | null;

  // Estado UI compartido
  sidebarOpen: boolean;

  // Errores de fetch
  error: Error | null;

  // Acciones
  setSelectedRestaurant: (id: string, slug: string) => void;
  setMesSel: (mes: { anio: number; mm: number } | null) => void;
  toggleSidebar: () => void;
}

const FilterContext = createContext<FilterCtx | null>(null);

// ── Provider ──────────────────────────────────────────────

export function FilterProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState<RestaurantInfo[]>([]);
  const [meses, setMeses] = useState<MesDisponible[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Leer selección desde URL (persiste entre tabs y tras recarga)
  const selectedRestaurantSlug = searchParams.get("restaurant") ?? "";
  const selectedRestaurantId = useMemo(() => {
    const found = restaurants.find((r) => r.slug === selectedRestaurantSlug);
    return found?.id ?? "";
  }, [restaurants, selectedRestaurantSlug]);

  const anioUrl = searchParams.get("anio");
  const mmUrl = searchParams.get("mm");
  const mesSel: { anio: number; mm: number } | null =
    anioUrl && mmUrl ? { anio: parseInt(anioUrl, 10), mm: parseInt(mmUrl, 10) } : null;

  // ── Fetch: restaurants (una vez) ──
  // Via apiClient y no fetch(): apiClient manda la cookie de sesion
  // (withCredentials). Con fetch sin credenciales la API responde 401 y el
  // selector quedaria vacio aunque el usuario este logueado (TASK-005).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: body } = await apiClient.get<{ restaurants: RestaurantInfo[] }>("/restaurants/");
        if (cancelled) return;
        const list: RestaurantInfo[] = body.restaurants || [];
        setRestaurants(list);
        // Fijar el restaurante de la URL si hace falta:
        //   - no hay ninguno, o
        //   - el de la URL NO esta entre los permitidos (por ejemplo un enlace
        //     viejo a un restaurante que el usuario ya no puede ver, o un slug
        //     ajeno escrito a mano). Sin esto el selector quedaba vacio y el
        //     tablero sin datos, en vez de caer al primero que SI puede ver.
        const enUrl = searchParams.get("restaurant");
        const permitido = enUrl && list.some((r) => r.slug === enUrl);
        if (list.length > 0 && !permitido) {
          setSearchParams(
            (prev) => {
              prev.set("restaurant", list[0].slug);
              prev.delete("anio");
              prev.delete("mm");
              return prev;
            },
            { replace: true }
          );
        }
      } catch (err) {
        if (!cancelled) setError(err as Error);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch: meses cuando cambia el restaurante ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: body } = await apiClient.get<{
          periodos: { anio: number; mm: number; label: string }[];
        }>("/catalogo/periodos", {
          params: { restaurant_id: selectedRestaurantSlug },
        });
        if (cancelled) return;
        const list: MesDisponible[] = (body.periodos || []).map((p) => ({
          anio: p.anio,
          mm: p.mm,
          label: p.label,
          n_dias: 0,
        }));
        setMeses(list);
        // Si no hay mes en URL y hay meses disponibles, poner el más reciente
        if (list.length > 0 && !searchParams.has("anio")) {
          setSearchParams(
            (prev) => {
              prev.set("anio", String(list[0].anio));
              prev.set("mm", String(list[0].mm));
              return prev;
            },
            { replace: true }
          );
        }
      } catch (err) {
        console.warn("Error fetching catalogo/periodos:", err);
        if (!cancelled) { setMeses([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSelectedRestaurant = useCallback((_id: string, slug: string) => {
    setSearchParams(
      (prev) => {
        prev.set("restaurant", slug);
        // No resetear mes al cambiar de restaurante (lo maneja el useEffect)
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const setMesSel = useCallback((mes: { anio: number; mm: number } | null) => {
    setSearchParams(
      (prev) => {
        if (mes) {
          prev.set("anio", String(mes.anio));
          prev.set("mm", String(mes.mm));
        } else {
          prev.delete("anio");
          prev.delete("mm");
        }
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const value = useMemo<FilterCtx>(() => ({
    restaurants,
    meses,
    selectedRestaurantId,
    selectedRestaurantSlug,
    mesSel,
    sidebarOpen,
    error,
    setSelectedRestaurant,
    setMesSel,
    toggleSidebar,
  }), [
    restaurants, meses, selectedRestaurantId, selectedRestaurantSlug,
    mesSel, sidebarOpen, error, setSelectedRestaurant, toggleSidebar,
  ]);

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────

export function useFilterContext(): FilterCtx {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error("useFilterContext debe usarse dentro de <FilterProvider>");
  return ctx;
}
